// Account Type Enums
export enum AccountType {
  // Partner Accounts
  MERCHANT_ACCOUNT = 'MERCHANT_ACCOUNT',
  RETAILER_ACCOUNT = 'RETAILER_ACCOUNT',
  AGENT_ACCOUNT = 'AGENT_ACCOUNT',

  // Control Accounts
  VOUCHER_CLEARING = 'VOUCHER_CLEARING',
  COMMISSION_REVENUE = 'COMMISSION_REVENUE',
  MERCHANT_PAYABLE = 'MERCHANT_PAYABLE',
  RETAILER_RECEIVABLE = 'RETAILER_RECEIVABLE',

  // Bank Accounts
  PSP_BANK_ACCOUNT = 'PSP_BANK_ACCOUNT',
  SETTLEMENT_ACCOUNT = 'SETTLEMENT_ACCOUNT',

  // Agent Pool control accounts (one per currency). Agent float is managed in an external
  // system; all agent collections debit these pooled accounts, which can go negative.
  AGENT_POOL_USD = 'AGENT_POOL_USD',
  AGENT_POOL_ZWG = 'AGENT_POOL_ZWG',
  AGENT_POOL_ZAR = 'AGENT_POOL_ZAR',

  // Other
  SUSPENSE_ACCOUNT = 'SUSPENSE_ACCOUNT',
  GENERAL_LEDGER = 'GENERAL_LEDGER',
}

export enum AccountNature {
  DEBIT = 'DEBIT',
  CREDIT = 'CREDIT',
}

export enum PostingType {
  DEBIT = 'DEBIT',
  CREDIT = 'CREDIT',
}

export enum FloatLoadType {
  BANK_TRANSFER = 'BANK_TRANSFER',
  CASH_DEPOSIT = 'CASH_DEPOSIT',
  MOBILE_MONEY = 'MOBILE_MONEY',
  CARD_PAYMENT = 'CARD_PAYMENT',
  REVERSAL = 'REVERSAL',
  ADJUSTMENT = 'ADJUSTMENT',
}

export enum TransactionStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  REVERSED = 'REVERSED',
}

// Account Entity
export interface Account {
  id: string;
  accountNumber: string;
  accountName: string;
  accountType: AccountType;
  accountNature: AccountNature;
  currency: string;
  partnerId?: string;
  partnerName?: string;
  partnerCode?: string;
  currentBalance: number;
  availableBalance: number;
  isActive: boolean;
  canHaveNegativeBalance: boolean;
  isControlAccount: boolean;
  dateCreated: string;
  lastUpdated: string;
  lastTransactionDate?: string;
  description?: string;
}

// Account Transaction Entity
export interface AccountTransaction {
  id: string;
  transactionRef: string;
  transactionReference?: string; // Alias for backward compatibility
  transactionType?: string;
  transactionDate: string;
  description: string;
  currency: string;
  totalAmount: number;
  status: TransactionStatus;
  transactionStatus?: string;
  voucherId?: string;
  voucherNumber?: string;
  isBalanced: boolean;
  numberOfPostings: number;
  createdBy?: string;
  dateCreated: string;
  postings?: AccountPosting[];
}

// Account Posting Entity
export interface AccountPosting {
  id: string;
  transactionId: string;
  transactionReference: string;
  accountId: string;
  accountNumber: string;
  accountName: string;
  postingType: PostingType;
  amount: number;
  currency: string;
  balanceBefore: number;
  balanceAfter: number;
  postingReference: string;
  description: string;
  postingDate: string;
  dateCreated: string;
  // Set only on postings to the agent pool control account: identify the collecting
  // agent and voucher for reconciliation with the external float system.
  agentCode?: string;
  voucherRef?: string;
}

// Float Load Entity
export interface FloatLoad {
  id: string;
  partnerId: string;
  partnerName?: string;
  partnerCode?: string;
  accountId: string;
  accountNumber: string;
  currency: string;
  amount: number;
  loadType: FloatLoadType;
  referenceNumber: string;
  bankReference?: string;
  depositDate?: string;
  transactionId?: string;
  transactionReference?: string;
  status: string;
  notes?: string;
  processedBy?: string;
  dateCreated: string;
  lastUpdated: string;
}

// Account Statement
export interface AccountStatement {
  account: Account;
  statementPeriod: {
    startDate: string;
    endDate: string;
  };
  openingBalance: number;
  closingBalance: number;
  totalDebits: number;
  totalCredits: number;
  transactionCount: number;
  postings: AccountPosting[];
}

// Request/Response DTOs

export interface AccountResponse {
  id: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  accountNature: string;
  currency: string;
  partnerId?: string;
  partnerName?: string;
  currentBalance: number;
  availableBalance: number;
  isActive: boolean;
  canHaveNegativeBalance: boolean;
  isControlAccount: boolean;
  dateCreated: string;
}

export interface FloatLoadRequest {
  partnerId: string;
  currency: string;
  amount: number;
  loadType: FloatLoadType;
  referenceNumber: string;
  bankReference?: string;
  depositDate?: string;
  notes?: string;
}

export interface TransactionResponse {
  id: string;
  transactionReference: string;
  transactionDate: string;
  description: string;
  currency: string;
  totalAmount: number;
  status: string;
  voucherNumber?: string;
  isBalanced: boolean;
  numberOfPostings: number;
  dateCreated: string;
}

export interface PostingResponse {
  id: string;
  transactionReference: string;
  accountNumber: string;
  accountName: string;
  postingType: string;
  amount: number;
  currency: string;
  balanceBefore: number;
  balanceAfter: number;
  description: string;
  postingDate: string;
  agentCode?: string;
  voucherRef?: string;
}

export interface AccountSearchParams {
  search?: string;
  accountType?: AccountType;
  currency?: string;
  partnerId?: string;
  isActive?: boolean;
  isControlAccount?: boolean;
  page?: number;
  size?: number;
}

export interface TransactionSearchParams {
  search?: string;
  startDate?: string;
  endDate?: string;
  currency?: string;
  partnerId?: string;
  accountNumber?: string;
  status?: string;
  type?: string;
  minAmount?: number;
  maxAmount?: number;
  page?: number;
  size?: number;
}

export interface PostingSearchParams {
  search?: string;
  startDate?: string;
  endDate?: string;
  accountNumber?: string;
  postingType?: PostingType;
  currency?: string;
  page?: number;
  size?: number;
}

export interface AccountStatementParams {
  accountNumber: string;
  startDate: string;
  endDate: string;
}

// Paginated Response
export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

// Account Type Display Labels
export const AccountTypeLabels: Record<AccountType, string> = {
  [AccountType.MERCHANT_ACCOUNT]: 'Merchant Account',
  [AccountType.RETAILER_ACCOUNT]: 'Retailer Account',
  [AccountType.AGENT_ACCOUNT]: 'Agent Account',
  [AccountType.VOUCHER_CLEARING]: 'Voucher Clearing',
  [AccountType.COMMISSION_REVENUE]: 'Commission Revenue',
  [AccountType.MERCHANT_PAYABLE]: 'Merchant Payable',
  [AccountType.RETAILER_RECEIVABLE]: 'Retailer Receivable',
  [AccountType.PSP_BANK_ACCOUNT]: 'PSP Bank Account',
  [AccountType.SETTLEMENT_ACCOUNT]: 'Settlement Account',
  [AccountType.AGENT_POOL_USD]: 'Agent Pool (USD)',
  [AccountType.AGENT_POOL_ZWG]: 'Agent Pool (ZWG)',
  [AccountType.AGENT_POOL_ZAR]: 'Agent Pool (ZAR)',
  [AccountType.SUSPENSE_ACCOUNT]: 'Suspense Account',
  [AccountType.GENERAL_LEDGER]: 'General Ledger',
};

// Float Load Type Labels
export const FloatLoadTypeLabels: Record<FloatLoadType, string> = {
  [FloatLoadType.BANK_TRANSFER]: 'Bank Transfer',
  [FloatLoadType.CASH_DEPOSIT]: 'Cash Deposit',
  [FloatLoadType.MOBILE_MONEY]: 'Mobile Money',
  [FloatLoadType.CARD_PAYMENT]: 'Card Payment',
  [FloatLoadType.REVERSAL]: 'Reversal',
  [FloatLoadType.ADJUSTMENT]: 'Adjustment',
};